import { Point } from "@/routes/Frontend/FuturebilityCockpit/types";
import { ReactNode, useState } from "react";
import { SidePanel } from "../../../FuturebilityCockpit/components/SidePanel";
import { UserIcon } from "../quadrant/shared";
import "./index.scss";
import {
  PersonalityMatrixAdvancedContent,
  PersonalityMatrixAdvancedLabels,
} from "./types";
import {
  getPercentagesByQuadrant,
  getPosition,
  interpolateColor,
} from "./utils";
import React from "react";

const HoverItem: React.FC<{
  hoverKey: keyof PersonalityMatrixAdvancedContent;
  hoveredKey: keyof PersonalityMatrixAdvancedContent | null;
  onHoverChange?: (key: keyof PersonalityMatrixAdvancedContent | null) => void;
  className: string;
  hoveredClassName?: string;
  children: ReactNode;
  style?: React.CSSProperties;
}> = ({
  hoverKey,
  hoveredKey,
  onHoverChange,
  className,
  hoveredClassName = "hovered",
  children,
  style,
  ...props
}) => {
  const isHovered = hoveredKey === hoverKey;
  const combinedClassName = `${className} ${
    isHovered ? hoveredClassName : ""
  }`.trim();

  const handleMouseEnter = () => {
    onHoverChange?.(hoverKey);
  };

  const handleMouseLeave = () => {
    onHoverChange?.(null);
  };

  return (
    <div
      className={combinedClassName}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={style}
      {...props}
    >
      {children}
    </div>
  );
};

interface PersonalityMatrixAdvancedProps {
  points: Point[];
  labels: PersonalityMatrixAdvancedLabels;
  content: PersonalityMatrixAdvancedContent;
  isClustered?: boolean;
}

interface InfoIconProps extends React.SVGProps<SVGSVGElement> {
  toRotate?: boolean;
  onHover?: (hoverKey: keyof PersonalityMatrixAdvancedContent | null) => void;
  hoverKey?: keyof PersonalityMatrixAdvancedContent;
}

const InfoIcon = (props: InfoIconProps) => {
  const { toRotate, onHover, hoverKey, ...rest } = props;

  const handleMouseEnter = () => {
    onHover?.(hoverKey || null);
  };

  const handleMouseLeave = () => {
    onHover?.(null);
  };

  return (
    <svg
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      {...rest}
      transform={toRotate ? "rotate(-90)" : undefined}
    >
      <g clipPath="url(#clip0_6002_2848)">
        <path
          d="M10.6666 7.99984L7.99998 7.99984M5.33331 7.99984L5.33331 7.99317M7.99998 1.33317C11.6819 1.33317 14.6666 4.31794 14.6666 7.99984C14.6666 11.6817 11.6819 14.6665 7.99998 14.6665C4.31808 14.6665 1.33331 11.6817 1.33331 7.99984C1.33331 4.31794 4.31808 1.33317 7.99998 1.33317Z"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
      <defs>
        <clipPath id="clip0_6002_2848">
          <rect width="16" height="16" fill="currentColor" />
        </clipPath>
      </defs>
    </svg>
  );
};

export const PersonalityMatrixAdvanced: React.FC<
  PersonalityMatrixAdvancedProps
> = ({
  points = [{ x: 0.7, y: 0.25 }],
  labels = {
    left: "left",
    leftSub: "leftSub",
    right: "right",
    rightSub: "rightSub",
    top: "top",
    topSub: "topSub",
    bottom: "bottom",
    bottomSub: "bottomSub",
    topLeft: "topLeft",
    topRight: "topRight",
    bottomLeft: "bottomLeft",
    bottomRight: "bottomRight",
    grid1: "grid1",
    grid2: "grid2",
    grid3: "grid3",
    grid4: "grid4",
    grid5: "grid5",
    grid6: "grid6",
    grid7: "grid7",
    grid8: "grid8",
    grid9: "grid9",
  },
  content = {
    left: "Left Sub Content",
    right: "Right Sub Content",
    top: "Top Sub Content",
    bottom: "Bottom Sub Content",
    topLeft: "Top Left Content",
    topRight: "Top Right Content",
    bottomLeft: "Bottom Left Content",
    bottomRight: "Bottom Right Content",
    grid1: "Grid 1 Content",
    grid2: "Grid 2 Content",
    grid3: "Grid 3 Content",
    grid4: "Grid 4 Content",
    grid5: "Grid 5 Content",
    grid6: "Grid 6 Content",
    grid7: "Grid 7 Content",
    grid8: "Grid 8 Content",
    grid9: "Grid 9 Content",
  },
  isClustered = false,
}) => {
  const [hoveredKey, setHoveredKey] = useState<
    keyof PersonalityMatrixAdvancedContent | null
  >(null);

  const percentagesByQuadrant = getPercentagesByQuadrant(points);

  const percentagesByGrid = Object.keys(percentagesByQuadrant)
    .filter(key => key.startsWith("grid"))
    .map(
      key =>
        percentagesByQuadrant[key as keyof PersonalityMatrixAdvancedContent] ??
        0
    );

  const minPercentage = Math.min(...percentagesByGrid);
  const maxPercentage = Math.max(...percentagesByGrid);

  const hoveredTitle = hoveredKey ? labels[hoveredKey] : null;
  const hoveredContent = hoveredKey ? content[hoveredKey] : null;

  return (
    <div
      className={`personality-matrix-advanced ${
        isClustered ? "clustered" : ""
      }`}
    >
      <div className="inner-container">
        <HoverItem
          hoverKey="left"
          hoveredKey={hoveredKey}
          className="main-label left"
        >
          <div className="main-label-inner">
            <div className="main-label-value">
              {labels.left} <InfoIcon onHover={setHoveredKey} hoverKey="left" />
            </div>
            <div>{labels.leftSub}</div>
          </div>
        </HoverItem>
        <div className="chart-container">
          <div className="axes">
            <HoverItem
              hoverKey="top"
              hoveredKey={hoveredKey}
              className="main-label axis top"
            >
              <div className="main-label-inner">
                <div>{labels.topSub}</div>
                <div className="main-label-value">
                  {labels.top}{" "}
                  <InfoIcon
                    toRotate={true}
                    onHover={setHoveredKey}
                    hoverKey="top"
                  />
                </div>
              </div>
            </HoverItem>
            <HoverItem
              hoverKey="bottom"
              hoveredKey={hoveredKey}
              className="main-label axis bottom"
            >
              <div className="main-label-inner">
                <div className="main-label-value">
                  {labels.bottom}{" "}
                  <InfoIcon
                    toRotate={true}
                    onHover={setHoveredKey}
                    hoverKey="bottom"
                  />
                </div>
                <div>{labels.bottomSub}</div>
              </div>
            </HoverItem>
          </div>

          <div className="corners">
            <div className="corner top-left" />
            <div className="corner top-right" />
            <div className="corner bottom-left" />
            <div className="corner bottom-right" />
          </div>

          <div className="outer-grid">
            <HoverItem
              hoverKey="topLeft"
              hoveredKey={hoveredKey}
              className="outer-grid-item top-left"
            >
              <div className="outer-grid-item-inner">
                <InfoIcon
                  toRotate={true}
                  onHover={setHoveredKey}
                  hoverKey="topLeft"
                />
                <div className="label">A</div>
              </div>
            </HoverItem>
            <HoverItem
              hoverKey="topRight"
              hoveredKey={hoveredKey}
              className="outer-grid-item top-right"
            >
              <div className="outer-grid-item-inner">
                <div className="label">B</div>
                <InfoIcon
                  toRotate={true}
                  onHover={setHoveredKey}
                  hoverKey="topRight"
                />
              </div>
            </HoverItem>
            <HoverItem
              hoverKey="bottomLeft"
              hoveredKey={hoveredKey}
              className="outer-grid-item bottom-left"
            >
              <div className="outer-grid-item-inner">
                <InfoIcon
                  toRotate={true}
                  onHover={setHoveredKey}
                  hoverKey="bottomLeft"
                />
                <div className="label">C</div>
              </div>
            </HoverItem>
            <HoverItem
              hoverKey="bottomRight"
              hoveredKey={hoveredKey}
              className="outer-grid-item bottom-right"
            >
              <div className="outer-grid-item-inner">
                <div className="label">D</div>
                <InfoIcon
                  toRotate={true}
                  onHover={setHoveredKey}
                  hoverKey="bottomRight"
                />
              </div>
            </HoverItem>
          </div>

          <div className="grid">
            {Array.from({ length: 9 }, (_, index) => {
              const gridKey: keyof PersonalityMatrixAdvancedContent = `grid${
                index + 1
              }` as keyof PersonalityMatrixAdvancedContent;
              const percentage = percentagesByGrid[index];
              const backgroundColor = isClustered
                ? interpolateColor(percentage, minPercentage, maxPercentage)
                : undefined;

              return (
                <HoverItem
                  key={index}
                  hoverKey={gridKey}
                  hoveredKey={hoveredKey}
                  onHoverChange={setHoveredKey}
                  className={`grid-item`}
                >
                  <div className="grid-bg-color" style={{ backgroundColor }} />
                  <div className={`grid-label`}>{labels[gridKey]}</div>
                  {isClustered && (
                    <div
                      className="grid-cluster-info"
                      style={{ backgroundColor }}
                    >
                      <div className="percentage-value">{percentage}%</div>
                      <UserIcon />
                    </div>
                  )}
                </HoverItem>
              );
            })}
          </div>

          <div className="cross-lines">
            <div className="cross-line horizontal" />
            <div className="cross-line vertical" />
          </div>

          {!isClustered &&
            points.map((p, i) => (
              <div
                key={i}
                className="data-point"
                style={getPosition(p.x, p.y)}
              />
            ))}
        </div>
        <HoverItem
          hoverKey="right"
          hoveredKey={hoveredKey}
          className="main-label right"
        >
          <div className="main-label-inner">
            <div>{labels.rightSub}</div>
            <div className="main-label-value">
              {labels.right}{" "}
              <InfoIcon onHover={setHoveredKey} hoverKey="right" />
            </div>
          </div>
        </HoverItem>
      </div>
      {hoveredKey && hoveredTitle && (
        <SidePanel active={true} title={hoveredTitle} direction="left">
          <div className="personality-matrix-advanced-percentage">
            {percentagesByQuadrant[hoveredKey] ?? 0}%
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="19"
              viewBox="0 0 18 19"
              fill="none"
            >
              <path
                d="M16.3175 18.0145L16.3175 16.166C16.3175 15.1856 15.928 14.2452 15.2347 13.5519C14.5413 12.8586 13.601 12.4691 12.6204 12.4691L5.22635 12.4691C4.24583 12.4691 3.30547 12.8586 2.61214 13.5519C1.91881 14.2452 1.5293 15.1856 1.5293 16.166L1.5293 18.0145"
                stroke="#000050"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M8.92154 8.77353C10.9634 8.77353 12.6186 7.11835 12.6186 5.07657C12.6186 3.03479 10.9634 1.37961 8.92154 1.37961C6.87971 1.37961 5.22449 3.03479 5.22449 5.07657C5.22449 7.11835 6.87971 8.77353 8.92154 8.77353Z"
                stroke="#000050"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <div dangerouslySetInnerHTML={{ __html: hoveredContent ?? "N/A" }} />
        </SidePanel>
      )}
    </div>
  );
};
